'use client'

import Button, { buttonClassNames } from '@components/primitives/Button'
import { Icon } from '@components/primitives/Icon'
import { FullSizedCloudflareImage } from '@components/primitives/Image/Cloudflare'
import {
  DefaultToastMessages,
  Toast,
  type ToastProps,
} from '@components/primitives/Toasts/Toast'
import { classMerge } from '@components/utilities/classMerge'
import { faCopy } from '@fortawesome/pro-regular-svg-icons/faCopy'
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons/faEnvelope'
import { UTM_MEDIUMS, UTM_SOURCES } from '@lib/browsers/utm/types'
import { RoutePrefix } from '@lib/routes'
import DynamicRoutes from '@lib/routes/DynamicRoutes'
import absoluteUrl from '@lib/routes/helpers/absoluteUrl'
import { appendUtmParameters } from '@lib/routes/helpers/appendUtmParameters'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { TrackingSources } from '@lib/tracking/TrackingSources'
import createSlugFromFacets from '@modules/trials/search/facetFilters/createSlugFromFacets'
import type { ApplySuccessPageProps } from 'app/p/apply-success/[token]/[step]/page'
import { useState } from 'react'

const REFER_A_FRIEND_PHOTO = '44b5bee0-faff-4e9d-142b-9b1cef725a00'

const createReferralLinkWithUtms = ({
  condition,
  utm_content,
}: {
  condition?: string
  utm_content: 'copy_link' | 'email_button'
}) => {
  // If the condition is null or an undesirable value, send them to the root search page
  if (!condition || condition?.includes('Healthy')) {
    return appendUtmParameters(
      absoluteUrl(RoutePrefix.SEARCH, { forceProduction: true }),
      {
        utm_content,
        utm_medium: UTM_MEDIUMS.referral,
        utm_source: UTM_SOURCES.applyFlowReferralScreen,
      },
    )
  }

  // Otherwise, send them to the search page with the condition
  const searchLanderSlug = createSlugFromFacets({
    condition,
  })

  return appendUtmParameters(
    absoluteUrl(DynamicRoutes.search(searchLanderSlug), {
      forceProduction: true,
    }),
    {
      utm_content,
      utm_medium: UTM_MEDIUMS.referral,
      utm_source: UTM_SOURCES.applyFlowReferralScreen,
    },
  )
}

export function ShareTrialStep(
  props: Pick<ApplySuccessPageProps, 'conditionName'>,
) {
  const [toastSettings, showToast] = useState<ToastProps | null>(null)

  const emailLink = createReferralLinkWithUtms({
    condition: props.conditionName,
    utm_content: 'email_button',
  })
  const copyLink = createReferralLinkWithUtms({
    condition: props.conditionName,
    utm_content: 'copy_link',
  })

  const copyLinkClick = () => {
    navigator.clipboard.writeText(copyLink)
    showToast({ ...DefaultToastMessages.copy, className: 'w-32 rounded-xl' })
    trackEvent(TrackingEvents.SHARE_TRIAL_VIA_COPY_LINK_CLICKED, {
      source: window.location.pathname.includes('apply2')
        ? TrackingSources.APPLY_FLOW_V3_EXPERIMENTAL
        : TrackingSources.APPLY_FLOW_V2,
      val: copyLink,
    })
  }

  const emailClick = () => {
    window.open(
      `mailto:?subject=Check out these treatment options &body=I wanted to share this with you. Check it out: ${emailLink}`,
      '_blank',
      'noopener,noreferrer',
    )
    trackEvent(TrackingEvents.SHARE_TRIAL_VIA_EMAIL_CLICKED, {
      source: TrackingSources.APPLY_FLOW_V2,
      val: emailLink,
    })
  }

  return (
    <>
      <Toast showToast={showToast} toastSettings={toastSettings} />

      <div className='flex w-full flex-col-reverse items-center gap-6 pb-48 md:flex-row md:gap-12'>
        <div className='flex w-full flex-col gap-6 md:w-2/3'>
          <h1 className='text-2xl font-bold text-neutral900 md:text-4xl'>
            Know someone looking for new options? Spread the word
          </h1>

          <p className='text-neutral600'>
            Research shows that many people who struggle with this condition
            have family or friends who do too. Share the link to help them learn
            about and apply for access to cutting-edge research.
          </p>

          <div className='flex flex-col gap-2'>
            <p className='font-bold'>Copy your invite link</p>
            <div className='flex w-full gap-4'>
              <div
                className='flex h-14 w-1/2 cursor-pointer items-center justify-center rounded-full bg-neutral100 px-6 shadow-inner hover:bg-neutral50 md:w-2/3'
                onClick={copyLinkClick}
              >
                <p className='line-clamp-1'>{copyLink}</p>
              </div>
              <Button
                className={classMerge(
                  buttonClassNames.primary.purple,
                  'h-14 w-1/2 rounded-full md:w-1/3',
                )}
                onClick={copyLinkClick}
              >
                <Icon className='mr-2' icon={faCopy} />
                Copy link
              </Button>
            </div>
          </div>
          <div className='flex flex-col gap-2'>
            <p className='font-bold'>Share via</p>
            <div className='flex w-full gap-4'>
              <Button
                className={classMerge(
                  buttonClassNames.outline.white,
                  'h-14 w-1/2 rounded-md',
                )}
                onClick={emailClick}
              >
                <Icon className='mr-2' icon={faEnvelope} />
                Email
              </Button>
            </div>
          </div>
        </div>

        <FullSizedCloudflareImage
          alt='Share the trial with a friend'
          className='w-1/3'
          imageId={REFER_A_FRIEND_PHOTO}
        />
      </div>
    </>
  )
}
